import { type CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { buildHtmlDocument, buildPreviewDocument, isFullHtmlDocument } from '../html-document';
import { applyDomMutation } from './dom/apply-mutation';
import {
    applyFontFamily,
    applyFontSize,
    applyForeColor,
    applyFormatBlock,
    applyHighlight,
    applyLink,
    applyList,
    insertHorizontalRule,
    insertImage,
    insertTextAtRange,
    toggleInlineFormat,
    unlinkAnchor
} from './dom/commands';
import { clearEditHistory, createEditHistory, pushEditSnapshot, redoEditSnapshot, undoEditSnapshot } from './dom/edit-history';
import { applyEmailRegionFill, readEmailRegionColors } from './dom/email-regions';
import { applyTextAlign } from './dom/formatting';
import { resolveFormatShortcutCommand } from './dom/keyboard-shortcuts';
import { insertSanitizedHtml, resolvePastePayload } from './dom/sanitize-paste';
import { elementFromSelectionNode, readSelectionStyle } from './dom/selection';
import { TABLE_ROW_CHROME_HOST_ATTRIBUTE, type TableRowChromeLabels, attachTableRowChrome } from './dom/table-row-chrome';
import {
    addTableColumn,
    addTableRow,
    applyCellBorders,
    applyCellFill,
    applyRowBorderBottom,
    deleteTable,
    deleteTableColumn,
    deleteTableRow,
    getNearestCell,
    handleVisualEditorTab,
    insertCellLineBreak,
    insertTable
} from './dom/tables';
import { useHtmlTemplateEditor } from './editor-api';
import type { VisualEditorCommand } from './editor-types';
import { HTML_VISUAL_EDITOR_STYLE_ATTRIBUTE, serializeEditedHtml } from './serialize-document';

export interface HtmlVisualEditorProps {
    value: string;
    onChange(_value: string): void;
    head?: string;
    iframeTitle?: string;
    iframeStyle?: CSSProperties;
}

const buildVisualSource = (body: string, head?: string): string => (head === undefined ? buildPreviewDocument(body) : buildHtmlDocument(head, body));

export const HtmlVisualEditor = (props: HtmlVisualEditorProps) => {
    const intl = useIntl();
    const { registerInsertHandler, registerCommandHandler, setSelectionStyle, notifyFontSizeSelection } = useHtmlTemplateEditor();
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const cleanupRef = useRef<(() => void) | null>(null);
    const selectionRangeRef = useRef<Range | null>(null);
    const historyRef = useRef(createEditHistory());
    const historyCoalesceRef = useRef(false);
    const lastSerializedValueRef = useRef(props.value);
    const lastHeadRef = useRef(props.head);
    const serializesFullDocumentRef = useRef(isFullHtmlDocument(props.value));
    const onChangeRef = useRef(props.onChange);
    const [source, setSource] = useState(() => buildVisualSource(props.value, props.head));
    onChangeRef.current = props.onChange;

    useEffect(() => {
        if (props.value === lastSerializedValueRef.current && props.head === lastHeadRef.current) return;

        serializesFullDocumentRef.current = isFullHtmlDocument(props.value);
        lastSerializedValueRef.current = props.value;
        lastHeadRef.current = props.head;
        setSource(buildVisualSource(props.value, props.head));
    }, [props.head, props.value]);

    useEffect(() => () => cleanupRef.current?.(), []);

    const getDocument = useCallback(() => iframeRef.current?.contentDocument ?? null, []);

    const emitSelectionStyle = useCallback(() => {
        const document = getDocument();
        if (!document) return;

        const selection = document.getSelection();
        const liveRange = selection?.rangeCount ? selection.getRangeAt(0) : null;
        const stored = selectionRangeRef.current;
        // When the live caret collapsed, keep reading style from the last non-collapsed edit range.
        const styleNode =
            liveRange && !liveRange.collapsed
                ? (selection?.anchorNode ?? null)
                : stored && !stored.collapsed
                  ? stored.commonAncestorContainer
                  : (selection?.anchorNode ?? null);

        setSelectionStyle({
            ...readSelectionStyle(document, styleNode),
            emailRegionColors: readEmailRegionColors(document)
        });
    }, [getDocument, setSelectionStyle]);

    const rememberSelection = useCallback(
        (options?: { allowCollapsed?: boolean }) => {
            const document = getDocument();
            const selection = document?.getSelection();
            if (!selection || selection.rangeCount === 0) return;

            const next = selection.getRangeAt(0).cloneRange();
            const stored = selectionRangeRef.current;
            const allowCollapsed = options?.allowCollapsed === true;

            // selectionchange often briefly reports a collapsed range while restoring; do not
            // throw away the last non-collapsed range used by toolbar formatting commands.
            if (!next.collapsed || allowCollapsed || !stored || stored.collapsed) {
                selectionRangeRef.current = next;
            }
            emitSelectionStyle();
        },
        [emitSelectionStyle, getDocument]
    );

    const restoreSelection = useCallback((): boolean => {
        const document = getDocument();
        const selection = document?.getSelection();
        const range = selectionRangeRef.current;
        if (!document || !selection || !range) return false;

        selection.removeAllRanges();
        selection.addRange(range);
        return true;
    }, [getDocument]);

    const placeCursorAtEnd = useCallback(() => {
        const document = getDocument();
        if (!document?.body) return;

        const range = document.createRange();
        range.selectNodeContents(document.body);
        range.collapse(false);

        const selection = document.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
        selectionRangeRef.current = range.cloneRange();
    }, [getDocument]);

    const emitChange = useCallback(() => {
        const document = getDocument();
        if (!document) return;

        const nextValue = serializeEditedHtml(document, serializesFullDocumentRef.current);
        if (nextValue === lastSerializedValueRef.current) return;

        lastSerializedValueRef.current = nextValue;
        onChangeRef.current(nextValue);
    }, [getDocument]);

    const focusEditor = useCallback(() => {
        const document = getDocument();
        if (!document?.body) return;

        document.body.focus();
        if (!restoreSelection()) {
            placeCursorAtEnd();
        }
    }, [getDocument, placeCursorAtEnd, restoreSelection]);

    const pushHistorySnapshot = useCallback(() => {
        const document = getDocument();
        if (!document?.body) return;
        // Persist editable content only — never snapshot table chrome buttons.
        pushEditSnapshot(historyRef.current, serializeEditedHtml(document, false));
    }, [getDocument]);

    const restoreBodySnapshot = useCallback(
        (snapshot: string) => {
            const document = getDocument();
            if (!document?.body) return;

            const chromeNodes = Array.from(document.querySelectorAll(`[${TABLE_ROW_CHROME_HOST_ATTRIBUTE}]`));
            chromeNodes.forEach((node) => node.remove());

            document.body.innerHTML = snapshot;

            chromeNodes.forEach((node) => {
                if (node.tagName === 'STYLE') {
                    document.head.appendChild(node);
                } else {
                    document.body.appendChild(node);
                }
            });

            selectionRangeRef.current = null;
            placeCursorAtEnd();
            rememberSelection();
            emitChange();
        },
        [emitChange, getDocument, placeCursorAtEnd, rememberSelection]
    );

    const undo = useCallback(() => {
        const document = getDocument();
        if (!document?.body) return;

        const previous = undoEditSnapshot(historyRef.current, serializeEditedHtml(document, false));
        if (previous === null) return;
        restoreBodySnapshot(previous);
    }, [getDocument, restoreBodySnapshot]);

    const redo = useCallback(() => {
        const document = getDocument();
        if (!document?.body) return;

        const next = redoEditSnapshot(historyRef.current, serializeEditedHtml(document, false));
        if (next === null) return;
        restoreBodySnapshot(next);
    }, [getDocument, restoreBodySnapshot]);

    const runDomMutation = useCallback(
        (mutate: (_document: Document, _range: Range | null) => void) => {
            const document = getDocument();
            if (!document) return;

            pushHistorySnapshot();
            focusEditor();
            applyDomMutation(document, selectionRangeRef.current, mutate);
            rememberSelection();
            emitChange();
        },
        [emitChange, focusEditor, getDocument, pushHistorySnapshot, rememberSelection]
    );

    const executeCommand = useCallback(
        (command: VisualEditorCommand) => {
            if (!getDocument()) return;

            switch (command.type) {
                case 'undo':
                    undo();
                    return;
                case 'redo':
                    redo();
                    return;
                case 'align':
                    runDomMutation((nextDocument, range) => applyTextAlign(nextDocument, range, command.align));
                    return;
                case 'insertTable':
                    runDomMutation((nextDocument, range) => insertTable(nextDocument, range, command.rows, command.cols));
                    return;
                case 'addRow':
                    runDomMutation((nextDocument, range) => addTableRow(nextDocument, range, command.position));
                    return;
                case 'deleteRow':
                    runDomMutation((nextDocument, range) => deleteTableRow(nextDocument, range));
                    return;
                case 'addColumn':
                    runDomMutation((nextDocument, range) => addTableColumn(nextDocument, range, command.position, command.columnIndex));
                    return;
                case 'deleteColumn':
                    runDomMutation((nextDocument, range) => deleteTableColumn(nextDocument, range, command.columnIndex));
                    return;
                case 'deleteTable':
                    runDomMutation((nextDocument, range) => deleteTable(nextDocument, range));
                    return;
                case 'formatBlock':
                    runDomMutation((nextDocument, range) => applyFormatBlock(nextDocument, range, command.block));
                    return;
                case 'foreColor':
                    runDomMutation((nextDocument, range) => applyForeColor(nextDocument, range, command.color));
                    return;
                case 'hiliteColor':
                    runDomMutation((nextDocument, range) => applyHighlight(nextDocument, range, command.color));
                    return;
                case 'emailRegionFill':
                    runDomMutation((nextDocument) => applyEmailRegionFill(nextDocument, command.region, command.color));
                    return;
                case 'fontSize':
                    runDomMutation((nextDocument, range) => applyFontSize(nextDocument, range, command.size));
                    return;
                case 'fontFamily':
                    runDomMutation((nextDocument, range) => applyFontFamily(nextDocument, range, command.family));
                    return;
                case 'rowBorderBottom':
                    runDomMutation((nextDocument, range) => applyRowBorderBottom(nextDocument, range, command.border));
                    return;
                case 'cellBorders':
                    runDomMutation((nextDocument, range) => applyCellBorders(nextDocument, range, command.edges, command.color));
                    return;
                case 'cellFill':
                    runDomMutation((nextDocument, range) => applyCellFill(nextDocument, range, command.color));
                    return;
                case 'bold':
                case 'italic':
                case 'underline':
                    runDomMutation((nextDocument, range) => toggleInlineFormat(nextDocument, range, command.type));
                    return;
                case 'insertUnorderedList':
                    runDomMutation((nextDocument, range) => applyList(nextDocument, range, 'ul'));
                    return;
                case 'insertOrderedList':
                    runDomMutation((nextDocument, range) => applyList(nextDocument, range, 'ol'));
                    return;
                case 'insertHorizontalRule':
                    runDomMutation((nextDocument, range) => insertHorizontalRule(nextDocument, range));
                    return;
                case 'link':
                    runDomMutation((nextDocument, range) => applyLink(nextDocument, range, { href: command.href, text: command.text, target: command.target }));
                    return;
                case 'unlink':
                    runDomMutation((nextDocument, range) => unlinkAnchor(nextDocument, range));
                    return;
                case 'image':
                    runDomMutation((nextDocument, range) => insertImage(nextDocument, range, command.src, command.alt));
                    return;
            }
        },
        [getDocument, redo, runDomMutation, undo]
    );

    const handleLoad = useCallback(() => {
        cleanupRef.current?.();
        clearEditHistory(historyRef.current);
        historyCoalesceRef.current = false;

        const document = getDocument();
        if (!document?.body) return;

        document.body.contentEditable = 'true';
        document.body.spellcheck = true;

        const style = document.createElement('style');
        style.setAttribute(HTML_VISUAL_EDITOR_STYLE_ATTRIBUTE, 'true');
        style.textContent = `
body[contenteditable="true"] {
    min-height: calc(100vh - 16px);
    cursor: text;
}
body[contenteditable="true"]:focus {
    outline: none;
}
`;
        document.head.appendChild(style);

        const insertClipboardContent = (html: string | null | undefined, plainText: string | null | undefined) => {
            const payload = resolvePastePayload(html, plainText);
            if (payload.html) {
                focusEditor();
                runDomMutation((nextDocument) => insertSanitizedHtml(nextDocument, payload.html));
                return;
            }

            if (!payload.text) return;

            focusEditor();
            runDomMutation((nextDocument, range) => insertTextAtRange(nextDocument, range, payload.text));
        };

        const handlePaste = (event: ClipboardEvent) => {
            const clipboard = event.clipboardData;
            if (!clipboard) return;

            const html = clipboard.getData('text/html');
            const text = clipboard.getData('text/plain');
            if (!html && !text) return;

            event.preventDefault();
            insertClipboardContent(html, text);
        };

        const handleDragOver = (event: DragEvent) => event.preventDefault();
        const handleDrop = (event: DragEvent) => {
            event.preventDefault();
            const transfer = event.dataTransfer;
            if (!transfer) return;

            insertClipboardContent(transfer.getData('text/html'), transfer.getData('text/plain'));
        };

        const handleBeforeInput = () => {
            if (historyCoalesceRef.current) return;
            pushHistorySnapshot();
            historyCoalesceRef.current = true;
            document.defaultView?.setTimeout(() => {
                historyCoalesceRef.current = false;
            }, 400);
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            const selection = document.getSelection();
            const range = selection?.rangeCount ? selection.getRangeAt(0) : selectionRangeRef.current;

            if (event.key === 'Tab') {
                const result = handleVisualEditorTab(document, event, range);
                if (result === 'add-row') {
                    runDomMutation((nextDocument, nextRange) => addTableRow(nextDocument, nextRange, 'below'));
                    focusEditor();
                    return;
                }

                rememberSelection();
                focusEditor();
                return;
            }

            if (event.key === 'Enter' && !event.metaKey && !event.ctrlKey && !event.altKey) {
                if (!getNearestCell(range?.startContainer ?? null)) return;

                event.preventDefault();
                runDomMutation((nextDocument, nextRange) => insertCellLineBreak(nextDocument, nextRange));
                return;
            }

            const formatCommand = resolveFormatShortcutCommand(event);
            if (formatCommand) {
                event.preventDefault();
                executeCommand(formatCommand);
                return;
            }

            const meta = event.metaKey || event.ctrlKey;
            if (!meta) return;

            const key = event.key.toLowerCase();
            if (key === 'z' && event.shiftKey) {
                event.preventDefault();
                redo();
                return;
            }
            if (key === 'z') {
                event.preventDefault();
                undo();
                return;
            }
            if (key === 'y') {
                event.preventDefault();
                redo();
            }
        };

        const rememberSelectionFromGesture = () => {
            const document = getDocument();
            const selection = document?.getSelection();
            const live = selection?.rangeCount ? selection.getRangeAt(0) : null;

            rememberSelection({ allowCollapsed: true });

            if (!document || !selection) return;

            // Collapsed caret with only computed (non-inline) size is usually a miss-click in
            // padding; do not reset the toolbar stepper back to body 16px.
            if (live?.collapsed) {
                let current = elementFromSelectionNode(selection.anchorNode) as HTMLElement | null;
                let hasInlineFontSize = false;
                while (current) {
                    if (current.style?.fontSize) {
                        hasInlineFontSize = true;
                        break;
                    }
                    current = current.parentElement;
                }
                if (!hasInlineFontSize) return;
            }

            const style = readSelectionStyle(document, selection.anchorNode ?? null);
            notifyFontSizeSelection(style.fontSize);
        };
        const rememberSelectionFromChange = () => {
            rememberSelection();
        };

        document.addEventListener('input', emitChange);
        document.addEventListener('beforeinput', handleBeforeInput);
        document.addEventListener('keydown', handleKeyDown, true);
        document.addEventListener('keyup', rememberSelectionFromGesture);
        document.addEventListener('mouseup', rememberSelectionFromGesture);
        document.addEventListener('selectionchange', rememberSelectionFromChange);
        document.addEventListener('paste', handlePaste);
        document.addEventListener('dragover', handleDragOver);
        document.addEventListener('drop', handleDrop);

        const tableChromeLabels: TableRowChromeLabels = {
            menu: intl.formatMessage({
                id: 'content-detail.editor.table-row-options',
                description: 'Accessible label for the table row options control in the visual HTML editor',
                defaultMessage: 'Table row options'
            }),
            addRowAbove: intl.formatMessage({
                id: 'content-detail.editor.add-row-above',
                description: 'Toolbar button label for inserting a table row above the current row in the visual editor',
                defaultMessage: 'Add row above'
            }),
            addRowBelow: intl.formatMessage({
                id: 'content-detail.editor.add-row-below',
                description: 'Toolbar button label for inserting a table row below the current row in the visual editor',
                defaultMessage: 'Add row below'
            }),
            deleteRow: intl.formatMessage({
                id: 'content-detail.editor.delete-row',
                description: 'Toolbar button label for deleting the current table row in the visual editor',
                defaultMessage: 'Delete row'
            }),
            addColumnBefore: intl.formatMessage({
                id: 'content-detail.editor.add-column-before',
                description: 'Toolbar button label for inserting a table column before the current column in the visual editor',
                defaultMessage: 'Add column before'
            }),
            addColumnAfter: intl.formatMessage({
                id: 'content-detail.editor.add-column-after',
                description: 'Toolbar button label for inserting a table column after the current column in the visual editor',
                defaultMessage: 'Add column after'
            }),
            deleteColumn: intl.formatMessage({
                id: 'content-detail.editor.delete-column',
                description: 'Toolbar button label for deleting the current table column in the visual editor',
                defaultMessage: 'Delete column'
            }),
            deleteTable: intl.formatMessage({
                id: 'content-detail.editor.delete-table',
                description: 'Toolbar button label for deleting the current table in the visual editor',
                defaultMessage: 'Delete table'
            }),
            rowBorder: intl.formatMessage({
                id: 'content-detail.editor.row-border',
                description: 'Toolbar button label for adding a light bottom border to the current table row',
                defaultMessage: 'Row border'
            }),
            clearRowBorder: intl.formatMessage({
                id: 'content-detail.editor.clear-row-border',
                description: 'Toolbar button label for removing the bottom border from the current table row',
                defaultMessage: 'Clear row border'
            })
        };

        const detachTableChrome = attachTableRowChrome(document, tableChromeLabels, (command, range) => {
            // Chrome menu clicks can leave a stale selectionRangeRef; prefer the explicit cell range.
            if (range) {
                selectionRangeRef.current = range.cloneRange();
            } else {
                rememberSelection({ allowCollapsed: true });
            }
            executeCommand(command);
        });

        cleanupRef.current = () => {
            detachTableChrome();
            document.removeEventListener('input', emitChange);
            document.removeEventListener('beforeinput', handleBeforeInput);
            document.removeEventListener('keydown', handleKeyDown, true);
            document.removeEventListener('keyup', rememberSelectionFromGesture);
            document.removeEventListener('mouseup', rememberSelectionFromGesture);
            document.removeEventListener('selectionchange', rememberSelectionFromChange);
            document.removeEventListener('paste', handlePaste);
            document.removeEventListener('dragover', handleDragOver);
            document.removeEventListener('drop', handleDrop);
        };
    }, [
        emitChange,
        executeCommand,
        focusEditor,
        getDocument,
        intl,
        notifyFontSizeSelection,
        pushHistorySnapshot,
        redo,
        rememberSelection,
        runDomMutation,
        undo
    ]);

    useEffect(() => {
        const insertToken = (token: string) => {
            if (!getDocument()) return;
            runDomMutation((nextDocument, range) => insertTextAtRange(nextDocument, range, token));
        };

        registerInsertHandler(insertToken);
        registerCommandHandler(executeCommand);
        return () => {
            registerInsertHandler(null);
            registerCommandHandler(null);
        };
    }, [executeCommand, getDocument, registerCommandHandler, registerInsertHandler, runDomMutation]);

    const sized = Boolean(props.iframeStyle);

    return (
        <div className={sized ? 'mx-4 mb-3 flex justify-center self-stretch overflow-auto' : 'mx-4 mb-3 flex min-h-[70vh] justify-center self-stretch'}>
            <iframe
                ref={iframeRef}
                title={
                    props.iframeTitle ??
                    intl.formatMessage({
                        id: 'content-detail.editor.visual-editor-iframe-title',
                        description: 'Accessible title for the iframe used to visually edit notification content HTML',
                        defaultMessage: 'Notification content visual editor'
                    })
                }
                className={sized ? 'border-border-default rounded-md border bg-white' : 'border-border-default h-[70vh] w-full rounded-md border bg-white'}
                style={props.iframeStyle}
                sandbox='allow-same-origin allow-popups allow-popups-to-escape-sandbox'
                srcDoc={source}
                onLoad={handleLoad}
            />
        </div>
    );
};
